import { useState, type FormEvent } from 'react';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { ContactFormData } from '../types';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';

interface ContactFormProps {
  className?: string;
  subject?: string;
}

export function ContactForm({ className = '', subject = '' }: ContactFormProps) {
  const [formData, setFormData] = useState<ContactFormData & { botcheck?: string }>({
    name: '',
    email: '',
    phone: '',
    subject: subject || '',
    message: '',
    botcheck: '',
  });
  
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const accessKey = import.meta.env.VITE_WEB3FORMS_KEY;
    
    if (!accessKey) {
      setStatus('error');
      setErrorMessage('Konfigurationsfehler: Web3Forms Key nicht gefunden.');
      return;
    }

    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: accessKey,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject || 'Kontaktanfrage von Website',
          message: formData.message,
          from_name: 'ConciergeUnit Website',
          to: 'info@conciergeunit.de',
          botcheck: '', // Honeypot field
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', botcheck: '' });
      } else {
        throw new Error(data.message || 'Ein Fehler ist aufgetreten');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (status === 'success') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" role="alert" aria-live="polite">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" aria-hidden="true" />
        <h3 className="text-2xl font-bold text-green-800 mb-2">Vielen Dank!</h3>
        <p className="text-green-700 mb-6">
          Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns so schnell wie möglich bei Ihnen melden.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="btn-primary"
        >
          Neue Nachricht senden
        </button>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit} 
      className={`space-y-6 ${className}`}
      aria-label="Kontaktformular"
    >
      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" role="alert" aria-live="assertive">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-red-700 text-sm">{errorMessage}</p>
        </div>
      )}

      {/* Honeypot Field - Hidden from users, visible to bots */}
      <div className="hidden" aria-hidden="true">
        <input 
          type="text" 
          name="botcheck" 
          tabIndex={-1} 
          autoComplete="off"
          value={formData.botcheck}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Name <span className="text-red-500" aria-label="Pflichtfeld">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            placeholder="Ihr vollständiger Name"
            autoComplete="name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            E-Mail <span className="text-red-500" aria-label="Pflichtfeld">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            placeholder="ihre@email.de"
            autoComplete="email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Telefon
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            placeholder="+49 123 456789"
            autoComplete="tel"
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
            Betreff <span className="text-red-500" aria-label="Pflichtfeld">*</span>
          </label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
          >
            <option value="">Bitte wählen...</option>
            <option value="Night-Auditor-Service">Night-Auditor-Service</option>
            <option value="Receptionsservice">Receptionsservice</option>
            <option value="Event-Service">Event-Service</option>
            <option value="Karriere & Jobs">Karriere & Jobs</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          Nachricht <span className="text-red-500" aria-label="Pflichtfeld">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors resize-none"
          placeholder="Wie können wir Ihnen helfen?"
        />
      </div>

      <div className="text-sm text-gray-600">
        <p>
          Mit dem Absenden stimmen Sie unserer{' '}
          <a href="/datenschutz" className="text-brand-600 hover:text-brand-800 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">
            Datenschutzerklärung
          </a>{' '}
          zu. Ihre Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet.
        </p>
      </div>

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="btn-primary w-full md:w-auto flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
        aria-busy={status === 'submitting'}
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            Wird gesendet...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" aria-hidden="true" />
            Nachricht senden
          </>
        )}
      </button>
    </form>
  );
}
